import { ExternalLink } from "lucide-react";
import { reviseElevateLearnerCopy } from "@/data/elevateLearnerCopy";
import type { S2PElevateSlide } from "@/data/s2pElevateAI";
import { RECORD_SKILL_EXAMPLES, RECORD_SKILL_SLIDE, RECORD_SKILL_TOOLS } from "@/data/elevateRecordSkill";

const screenshots = reviseElevateLearnerCopy([
  { src: "/screenshots/elevate/claude-record-menu.webp", alt: "Claude Add skill menu with Record your screen", caption: "1. Claude: open the Add menu", width: 302, height: 330 },
  { src: "/screenshots/elevate/claude-record-setup.webp", alt: "Claude Record a skill dialog with Start recording and optional voice narration", caption: "2. Claude: review recording setup", width: 500, height: 248 },
]);
const examples = reviseElevateLearnerCopy(RECORD_SKILL_EXAMPLES);

export const ElevateRecordSkill = ({ slide = RECORD_SKILL_SLIDE }: { slide?: S2PElevateSlide }) => (
  <div data-layout="record-skill" className="mx-auto flex min-h-full w-full max-w-[1600px] flex-col justify-center py-2 text-[#0A1F44]">
    <header>
      <p className="text-[11px] font-extrabold uppercase tracking-[.16em] text-[#0047FF]">{slide.eyebrow}</p>
      <h1 className="s2p-editorial mt-2 text-[clamp(2rem,3.5vw,3.65rem)] font-bold leading-[1.02] tracking-[-.04em]">{slide.title}</h1>
      <p className="mt-2 text-sm leading-snug text-slate-700 lg:text-base">{slide.lead}</p>
    </header>

    <div className="mt-4 grid gap-4 border-y border-[#0A1F44]/20 py-3 sm:grid-cols-2 sm:gap-6">
      {RECORD_SKILL_TOOLS.map((tool) => (
        <section key={tool.name}>
          <h2 className="text-xs font-extrabold text-[#0047FF]">{tool.name}</h2>
          <p className="mt-1 text-sm font-bold leading-snug lg:text-base">{tool.path}</p>
          <p className="mt-1 text-xs leading-snug text-slate-600">{tool.detail} <a href={tool.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#0047FF] underline underline-offset-2">Official guide<ExternalLink aria-hidden="true" className="h-3 w-3" /></a></p>
        </section>
      ))}
    </div>

    <div className="mt-4 grid gap-5 lg:grid-cols-[1.1fr_1fr]">
      <section aria-label="Claude recording screenshots">
        <div className="grid grid-cols-[.8fr_1.2fr] gap-3">
          {screenshots.map((shot) => (
            <figure key={shot.src} className="min-w-0 border border-[#C8D3E5] bg-white">
              <a href={shot.src} target="_blank" rel="noopener noreferrer" aria-label={`Enlarge screenshot: ${shot.caption}`} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0047FF]">
                <img src={shot.src} alt={shot.alt} width={shot.width} height={shot.height} className="h-[clamp(9rem,22svh,14rem)] w-full object-contain" />
              </a>
              <figcaption className="border-t border-[#C8D3E5] px-2 py-2 text-[10px] leading-snug text-slate-600">{shot.caption}</figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-2 text-[10px] text-slate-500">Claude screenshots · September 2026 · Select to enlarge</p>
        <p className="mt-3 text-xs leading-snug text-slate-700"><strong>Narrate the rules:</strong> say which dates, files, and recipients change. Stop recording. Check the generated Skill and fix the steps before saving.</p>
      </section>

      <section aria-label="Skill recording examples" className="divide-y divide-[#0A1F44]/15">
        {examples.map((example) => (
          <div key={example.title} className="pb-3 pt-3 first:pt-0 last:pb-0">
            <h2 className="s2p-editorial text-xl font-bold">{example.title}</h2>
            <p className="mt-1 text-xs leading-snug text-slate-700 lg:text-sm"><strong>Demonstrate:</strong> {example.demonstrate}</p>
            <p className="mt-2 border-l-2 border-[#0047FF] pl-3 text-xs leading-snug text-slate-700 lg:text-sm"><strong>Session 4 test with a new input:</strong> “{example.replay}”</p>
          </div>
        ))}
      </section>
    </div>

    <aside className="mt-3 bg-[#0A1F44] px-4 py-2 text-xs leading-snug text-white sm:text-sm">
      <strong>Carry the Skill forward:</strong> use an approved sample and keep secrets off screen. Try new inputs in Session 4. Improve the instructions and rerun that test in Session 5. Can't record? Write the steps as a Skill.
    </aside>
  </div>
);
